import { Maximize, Minus, Plus } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import type { LabelLayoutType } from '@aeris/contracts'
import { renderLayoutSheetHtml } from '@aeris/printing'
import { Button } from '@aeris/ui'

import { PX_PER_MM, withZoom } from '../../lib/label-preview.ts'
import { printedOnToday } from '../../lib/printed-on.ts'

import { Segmented } from './controls.tsx'

/**
 * A prévia do editor de etiqueta (F129): grande, no centro, e clicável.
 *
 * É o MESMO `renderLayoutSheetHtml` que imprime, com `interactive` — cada
 * campo marcado com o seu índice — para que clicar no preço selecione o preço.
 * O `iframe` com `srcDoc` é da mesma origem, então a tela ouve o clique lá
 * dentro sem mensagem entre janelas.
 */

/** Um produto de exemplo com TODOS os campos preenchidos. */
const SAMPLE = {
  name: 'Bateria iPhone 11 Original',
  sku: '000123',
  gtin: '7891000315507',
  salePrice: 'R$ 189,90',
  salePriceAmount: '189.900000',
  lastPurchaseAt: '01/09/2026',
  brand: 'Apple',
  unit: 'UN',
  supplierReference: 'WEFIX',
  location: 'Gaveta 3',
}

/** Folga em volta do adesivo na vista de uma etiqueta, em milímetros. */
const PAD_MM = 3

const MIN_ZOOM = 0.2
const MAX_ZOOM = 8

type Mode = 'label' | 'sheet'

export const PreviewCanvas = ({
  layout,
  selected,
  onSelect,
  onKey,
}: {
  layout: LabelLayoutType
  selected: number | null
  onSelect: (index: number | null) => void
  /** Teclas apertadas com o foco dentro da prévia (setas, Delete, Esc). */
  onKey: (event: KeyboardEvent) => void
}): ReactNode => {
  const [mode, setMode] = useState<Mode>('label')
  // `null` é "ajustar à janela" — e continua ajustando quando a janela muda.
  const [scale, setScale] = useState<number | null>(null)
  const [area, setArea] = useState({ width: 600, height: 400 })

  const region = useRef<HTMLDivElement>(null)
  const frame = useRef<HTMLIFrameElement>(null)
  // Os tratadores mais recentes: o `iframe` recarrega a cada mudança, mas o
  // ouvinte registrado no carregamento não pode chamar uma versão velha.
  const handlers = useRef({ onSelect, onKey })
  useEffect(() => {
    handlers.current = { onSelect, onKey }
  })

  useEffect(() => {
    const element = region.current
    // Sem `ResizeObserver` (navegador embutido antigo, ambiente de teste), a
    // prévia fica no tamanho inicial: o zoom manual continua funcionando.
    if (element === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined) {
        setArea({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  const isRoll = layout.rows === 1
  const shown: LabelLayoutType =
    mode === 'label'
      ? {
          ...layout,
          page: {
            ...layout.page,
            marginLeftMm: PAD_MM,
            marginTopMm: PAD_MM,
            marginRightMm: PAD_MM,
            marginBottomMm: PAD_MM,
          },
          columns: 1,
          rows: 1,
        }
      : layout
  const rowsShown = isRoll ? 4 : layout.rows
  const count = mode === 'label' ? 1 : Math.min(layout.columns * rowsShown, 300)

  const sightWidthMm =
    mode === 'label' ? layout.label.widthMm + 2 * PAD_MM : layout.page.widthMm
  const sightHeightMm =
    mode === 'label'
      ? layout.label.heightMm + 2 * PAD_MM
      : isRoll
        ? layout.page.marginTopMm +
          layout.label.heightMm * rowsShown +
          layout.page.marginBottomMm
        : layout.page.heightMm

  const fit = Math.min(
    MAX_ZOOM,
    Math.max(
      MIN_ZOOM,
      Math.min(
        (area.width - 16) / (sightWidthMm * PX_PER_MM),
        (area.height - 16) / (sightHeightMm * PX_PER_MM)
      )
    )
  )
  const zoom = scale ?? fit

  const html = withZoom(
    renderLayoutSheetHtml(
      shown,
      Array.from({ length: count }, () => SAMPLE),
      selected === null
        ? { interactive: true, printedOn: printedOnToday() }
        : {
            interactive: true,
            highlight: selected,
            printedOn: printedOnToday(),
          }
    ),
    zoom
  )

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-(--border-subtle) px-4 py-2">
        <Segmented
          label="O que mostrar"
          value={mode}
          options={[
            { value: 'label', label: 'Uma etiqueta', title: 'Uma etiqueta' },
            {
              value: 'sheet',
              label: isRoll ? 'Bobina' : 'Folha inteira',
              title: isRoll ? 'Bobina' : 'Folha inteira',
            },
          ]}
          onChange={(next) => {
            setMode(next)
            setScale(null)
          }}
        />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Diminuir o zoom"
            leadingIcon={<Minus aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setScale(Math.max(MIN_ZOOM, zoom / 1.25))
            }}
          />
          <span className="w-12 text-center text-(length:--text-xs) text-(--text-secondary) tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Aumentar o zoom"
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setScale(Math.min(MAX_ZOOM, zoom * 1.25))
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={scale === null}
            leadingIcon={<Maximize aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setScale(null)
            }}
          >
            Ajustar
          </Button>
        </div>
      </div>

      <div
        ref={region}
        className="flex min-h-[18rem] flex-1 overflow-auto bg-(--surface-sunken) p-6"
      >
        <iframe
          ref={frame}
          title="Pré-visualização do modelo"
          srcDoc={html}
          onLoad={() => {
            const doc = frame.current?.contentDocument
            if (doc === null || doc === undefined) return
            doc.addEventListener('click', (event) => {
              const target = event.target as Element | null
              const hit =
                target !== null && typeof target.closest === 'function'
                  ? target.closest('[data-element]')
                  : null
              handlers.current.onSelect(
                hit === null ? null : Number(hit.getAttribute('data-element'))
              )
            })
            doc.addEventListener('keydown', (event) => {
              handlers.current.onKey(event)
            })
          }}
          style={{
            width: `${String(Math.ceil(sightWidthMm * PX_PER_MM * zoom) + 2)}px`,
            height: `${String(Math.ceil(sightHeightMm * PX_PER_MM * zoom) + 2)}px`,
          }}
          className="m-auto shrink-0 rounded-(--radius-sm) bg-white shadow-md"
        />
      </div>

      <p className="border-t border-(--border-subtle) px-4 py-2 text-(length:--text-xs) text-(--text-muted)">
        Clique num campo para editar. Setas movem 0,5 mm (Shift: 5 mm), Delete
        exclui. O contorno azul e o tracejado não saem no papel.
      </p>
    </div>
  )
}
